import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  FormControl,
  VStack,
  Link,
  Text,
  useColorModeValue,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { MeContext } from '../../../context/MeContext';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  selectFilterValues,
  updateFilterValues,
  updateTotalActiveFilters,
  setNewFiltersSet,
} from '../../../store/redux/slices/passList.slice';
import {
  useListGroupTagsQuery,
  useLazyListGroupTagsQuery,
} from '../../../store/redux/api/passApi';
import {
  CustomReactSelect,
  formatOptionLabelSelectPassType,
  formatOptionLabelFilterGroupTag,
} from '../../common/CustomReactSelect';
import CustomSearchableSelect from '../../common/CustomSearchableSelect';
import type { MeContextState } from '../../../store/types/account';

type LabelValueOption = { label: string; value: string };

interface PassesListFilterProps {
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

function PassesListFilter(props: PassesListFilterProps) {
  const dispatch = useAppDispatch();
  const filterValues = useAppSelector(selectFilterValues);
  const internalDisclosure = useDisclosure();
  const isOpen = props.isOpen ?? internalDisclosure.isOpen;
  const onClose = props.onClose ?? internalDisclosure.onClose;
  const [selectedGroupTags, setSelectedGroupTags] = useState([]);
  const [selectedPassType, setSelectedPassType] = useState<LabelValueOption[]>(
    []
  );
  const [selectedDeviceType, setSelectedDeviceType] =
    useState<LabelValueOption | null>(null);
  const [selectedAddedToWalletDate, setSelectedAddedToWalletDate] =
    useState('');
  const modeBoxBg = useColorModeValue('white', 'secondaryDark');
  const meCtx = useContext(MeContext) as { state: MeContextState };
  const { filterEntity } = meCtx.state;

  const passTypes = [
    { label: 'Generic', value: 'generic' },
    { label: 'Loyalty', value: 'storeCard' },
    { label: 'Event', value: 'eventTicket' },
    { label: 'Stamp card', value: 'stampCard' },
  ];

  // Fetch group tags via RTK Query
  const { data: groupTagsData } = useListGroupTagsQuery(
    { entityId: filterEntity?.uuid },
    { refetchOnMountOrArgChange: true }
  );
  const [triggerSearchGroupTags] = useLazyListGroupTagsQuery();

  const groupTagsList = useMemo(() => {
    const tags = groupTagsData?.data;
    if (!tags?.length) return [];
    return tags.map((tag) => ({ label: tag.id, value: tag.id }));
  }, [groupTagsData]);

  // Sync local state when Redux filter values are cleared externally (e.g. QuickFilters X button)
  useEffect(() => {
    setSelectedPassType(
      filterValues.passType?.length
        ? filterValues.passType.map((pt) => {
            const match = passTypes.find((p) => p.value === pt);
            return match || { label: pt, value: pt };
          })
        : []
    );
    setSelectedDeviceType(
      filterValues.deviceType
        ? { label: filterValues.deviceType, value: filterValues.deviceType }
        : null
    );
    setSelectedGroupTags(
      filterValues.groupTag?.length
        ? filterValues.groupTag.map((tag) => ({ label: tag, value: tag }))
        : []
    );
    setSelectedAddedToWalletDate(filterValues.addedToWalletDate || '');
  }, [filterValues]);

  const filterEntityRef = useRef(filterEntity);

  useEffect(() => {
    if (filterEntity !== filterEntityRef.current) {
      filterEntityRef.current = filterEntity;
      onResetAll();
    }
  }, [filterEntity]);

  const resetToCommitted = () => {
    setSelectedGroupTags(
      filterValues.groupTag?.length
        ? filterValues.groupTag.map((tag) => ({ label: tag, value: tag }))
        : []
    );
    setSelectedPassType(
      filterValues.passType?.length
        ? filterValues.passType.map((pt) => {
            const match = passTypes.find((p) => p.value === pt);
            return match || { label: pt, value: pt };
          })
        : []
    );
    setSelectedDeviceType(
      filterValues.deviceType
        ? { label: filterValues.deviceType, value: filterValues.deviceType }
        : null
    );
    setSelectedAddedToWalletDate(filterValues.addedToWalletDate || '');
  };

  const handleClose = () => {
    resetToCommitted();
    onClose();
  };

  const onSubmit = () => {
    const values = {
      ...filterValues,
      groupTag: selectedGroupTags?.length
        ? selectedGroupTags.map((tag) => tag.label)
        : [],
      passType: selectedPassType.map((pt) => pt.value),
      deviceType: selectedDeviceType?.value ?? '',
      addedToWalletDate: selectedAddedToWalletDate,
    };
    dispatch(updateFilterValues(values));

    const countableFilterValues =
      [values.deviceType, values.addedToWalletDate].filter(Boolean).length +
      (values.passType?.length ? 1 : 0) +
      (values.groupTag?.length ? 1 : 0);
    dispatch(updateTotalActiveFilters(countableFilterValues));
    dispatch(setNewFiltersSet(true));
    onClose();
  };

  const onResetAll = () => {
    dispatch(
      updateFilterValues({
        ...filterValues,
        groupTag: [],
        passType: [],
        deviceType: '',
        addedToWalletDate: '',
      })
    );
    dispatch(updateTotalActiveFilters(0));
    onClose();
    setSelectedGroupTags([]);
    setSelectedPassType([]);
    setSelectedDeviceType(null);
    setSelectedAddedToWalletDate('');
  };

  return (
    <>
      <Drawer onClose={handleClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent bg={modeBoxBg} maxW="250px">
          <DrawerHeader pt={{ base: '16px', lg: '56px' }} bg="#F0F0F0">
            <Text textStyle="headingFamilyRegular" fontSize="24px">
              More filters
            </Text>
          </DrawerHeader>
          <DrawerBody p="0" bg="#F0F0F0">
            <VStack spacing={8}>
              {/* by pass type */}
              <FormControl px="24px" mt="12px">
                <Text textStyle="bodyFamilyMedium" fontSize="16px">
                  Pass type
                </Text>
                <CustomReactSelect
                  options={passTypes}
                  formatOptionLabel={formatOptionLabelSelectPassType}
                  placeholder="Select"
                  onChange={(passType) =>
                    setSelectedPassType(passType ? [...passType] : [])
                  }
                  value={selectedPassType}
                  id="passTypeFilter"
                  isMulti
                  moreFilters
                />
              </FormControl>

              {/* by group tag */}
              <FormControl px="24px">
                <Text textStyle="bodyFamilyMedium" fontSize="16px">
                  Group tags
                </Text>
                <CustomSearchableSelect
                  options={groupTagsList}
                  placeholder="Select"
                  formatOptionLabel={formatOptionLabelFilterGroupTag}
                  onChange={(groupTags) => {
                    setSelectedGroupTags(groupTags);
                  }}
                  loadOptions={async (searchTerm) => {
                    try {
                      const result = await triggerSearchGroupTags({
                        entityId: filterEntity?.uuid,
                        tag: searchTerm,
                      }).unwrap();
                      return (result?.data || []).map((tag) => ({
                        label: tag.id,
                        value: tag.id,
                      }));
                    } catch (error) {
                      console.error('Error loading group tags:', error);
                      return [];
                    }
                  }}
                  value={selectedGroupTags}
                  isMulti
                  moreFilters
                />
              </FormControl>

              {/* by device type */}
              <FormControl px="24px">
                <Text textStyle="bodyFamilyMedium" fontSize="16px">
                  Device type
                </Text>
                <RadioGroup
                  display="flex"
                  flexDirection="column"
                  gap={4}
                  mt={4}
                  ml={4}
                  sx={{
                    '.chakra-radio__control[data-checked]': {
                      bg: '#F0F0F0',
                      borderColor: '#505E69',
                      color: '#505E69',
                      _hover: { bg: '#F0F0F0', borderColor: '#505E69' },
                      _before: { bg: '#505E69' },
                    },
                    '.chakra-radio__control:hover': {
                      borderColor: '#505E69',
                    },
                  }}
                  value={selectedDeviceType?.value ?? ''}
                  onChange={() => {}}
                >
                  <Radio
                    value="iphone"
                    onClick={() =>
                      setSelectedDeviceType(
                        selectedDeviceType?.value === 'iphone'
                          ? null
                          : { label: 'iphone', value: 'iphone' }
                      )
                    }
                  >
                    Apple
                  </Radio>
                  <Radio
                    value="android"
                    onClick={() =>
                      setSelectedDeviceType(
                        selectedDeviceType?.value === 'android'
                          ? null
                          : { label: 'android', value: 'android' }
                      )
                    }
                  >
                    Google
                  </Radio>
                </RadioGroup>
              </FormControl>

              {/* by date installed */}
              <FormControl px="24px">
                <Text textStyle="bodyFamilyMedium" fontSize="16px">
                  Date installed
                </Text>
                <RadioGroup
                  display="flex"
                  flexDirection="column"
                  gap={4}
                  mt={4}
                  ml={4}
                  sx={{
                    '.chakra-radio__control[data-checked]': {
                      bg: '#F0F0F0',
                      borderColor: '#505E69',
                      color: '#505E69',
                      _hover: { bg: '#F0F0F0', borderColor: '#505E69' },
                      _before: { bg: '#505E69' },
                    },
                    '.chakra-radio__control:hover': {
                      borderColor: '#505E69',
                    },
                  }}
                  value={selectedAddedToWalletDate}
                  onChange={() => {}}
                >
                  <Radio
                    value="today"
                    onClick={() =>
                      setSelectedAddedToWalletDate(
                        selectedAddedToWalletDate === 'today' ? '' : 'today'
                      )
                    }
                  >
                    Today
                  </Radio>
                  <Radio
                    value="7days"
                    onClick={() =>
                      setSelectedAddedToWalletDate(
                        selectedAddedToWalletDate === '7days' ? '' : '7days'
                      )
                    }
                  >
                    Last 7 days
                  </Radio>
                  <Radio
                    value="30days"
                    onClick={() =>
                      setSelectedAddedToWalletDate(
                        selectedAddedToWalletDate === '30days' ? '' : '30days'
                      )
                    }
                  >
                    Last 30 days
                  </Radio>
                </RadioGroup>
              </FormControl>
            </VStack>
          </DrawerBody>
          <DrawerFooter bg="#F0F0F0">
            <Link
              color="#9B9B9B"
              fontSize="14px"
              onClick={onResetAll}
              mr="30px"
            >
              <Text textStyle="bodyFamilyMedium">Reset all</Text>
            </Link>
            <Button
              type="button"
              aria-label="Apply"
              size="sm"
              px="30px"
              alignSelf="flex-start"
              onClick={onSubmit}
            >
              Apply
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default PassesListFilter;
